import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FC, Fragment, useMemo, useState } from 'react';
import {
  HiChevronDown,
  HiChevronRight,
  HiQuestionMarkCircle,
} from 'react-icons/hi2';
import { KidTemplateSetupStatus } from '../../../../../api/kids/types';
import GenericDropdown from '../../../../ui/GenericDropDown';
import GenericModal from '../../../../ui/GenericModal';
import SearchBar from '../../../../ui/inputs/SearchBar';
import {
  btnPrimary,
  formatKidTemplateTypeLabel,
  formatTemplateSetupStatusLabel,
  listToolbarDropdownClassName,
  listToolbarDropdownMenuClassName,
  sortTemplateTypesByRank,
  templateSetupStatusBadgeClass,
} from '../templateManagement.constants';
import { TemplateListTableRow } from '../templateManagement.types';
import {
  buildTemplateListGroups,
  countTemplatesWithDocumentIssues,
  templateListSubgroupCollapseKey,
} from '../templateManagement.utils';
import { getTemplateSetupStatusPills } from '../templateManagement.helpers';
import { TemplateMoreInformationContent } from './TemplateMoreInformation';
import TemplateSetupStatusPills from './TemplateSetupStatusPills';
import TemplateTypeChip from './TemplateTypeChip';

const ALL_TYPES_FILTER = 'all';
const ALL_STATUSES_FILTER = 'all';

type TypeFilterOption = {
  value: string;
  label: string;
};

type StatusFilterOption = {
  value: string;
  label: string;
};

const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: ALL_STATUSES_FILTER, label: 'All statuses' },
  {
    value: KidTemplateSetupStatus.INCOMPLETE,
    label: formatTemplateSetupStatusLabel(KidTemplateSetupStatus.INCOMPLETE),
  },
  {
    value: KidTemplateSetupStatus.COMPLETE,
    label: formatTemplateSetupStatusLabel(KidTemplateSetupStatus.COMPLETE),
  },
  {
    value: KidTemplateSetupStatus.INVALID,
    label: formatTemplateSetupStatusLabel(KidTemplateSetupStatus.INVALID),
  },
];

const StatusFilterLabel: FC<{ status: string; label: string }> = ({
  status,
  label,
}) => {
  if (status === ALL_STATUSES_FILTER) {
    return <>{label}</>;
  }

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium capitalize ring-1 ${templateSetupStatusBadgeClass(status)}`}
    >
      {label}
    </span>
  );
};

/** Issue counts for toolbar and group headers. */
const TemplateIssueCount: FC<{
  issueCount: number;
  isUpdating?: boolean;
  className?: string;
}> = ({ issueCount, isUpdating = false, className }) => {
  if (isUpdating) {
    return (
      <span className={`text-sm text-gray-500 ${className ?? ''}`.trim()}>
        Updating counts…
      </span>
    );
  }

  if (issueCount <= 0) return null;

  return (
    <span
      className={`inline-flex items-baseline gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-xs ring-1 ring-amber-200/90 ${className ?? ''}`.trim()}
      title={`${issueCount} template${issueCount === 1 ? '' : 's'} with document issues`}
    >
      <span className="font-semibold tabular-nums text-amber-900">
        {issueCount}
      </span>
      <span className="font-medium text-amber-800/80">
        issue{issueCount === 1 ? '' : 's'}
      </span>
    </span>
  );
};

const TemplateListPanel: FC<{
  rows: TemplateListTableRow[];
  isUpdatingCounts: boolean;
  onSelectTemplate: (templateId: string) => void;
  onStartCreateTemplate: () => void;
}> = ({
  rows,
  isUpdatingCounts,
  onSelectTemplate,
  onStartCreateTemplate,
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES_FILTER);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES_FILTER);
  const [collapsedTypeKeys, setCollapsedTypeKeys] = useState<Set<string>>(
    () => new Set(),
  );
  /** `null` = default: collapse reference groups when a type has more than one. */
  const [collapsedSubgroupKeys, setCollapsedSubgroupKeys] = useState<
    Set<string> | null
  >(null);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return rows.filter((row) => {
      if (typeFilter !== ALL_TYPES_FILTER && row.typeKey !== typeFilter) {
        return false;
      }

      if (statusFilter !== ALL_STATUSES_FILTER) {
        const matchesStatus = getTemplateSetupStatusPills({
          setupStatus: row.setupStatus,
          missingCount: row.missingDocumentCount,
          invalidCount: row.invalidDocumentCount,
        }).some((pill) => pill.status === statusFilter);
        if (!matchesStatus) return false;
      }

      if (!query) return true;

      const templateName = (
        row.template.template_name || row.template.template_id
      ).toLowerCase();
      const statusLabels = getTemplateSetupStatusPills({
        setupStatus: row.setupStatus,
        missingCount: row.missingDocumentCount,
        invalidCount: row.invalidDocumentCount,
      })
        .map((pill) => formatTemplateSetupStatusLabel(pill.status))
        .join(' ');

      return (
        templateName.includes(query) ||
        row.appliesToLabel.toLowerCase().includes(query) ||
        formatKidTemplateTypeLabel(row.typeKey).toLowerCase().includes(query) ||
        statusLabels.toLowerCase().includes(query)
      );
    });
  }, [rows, searchQuery, typeFilter, statusFilter]);

  const groups = useMemo(
    () => buildTemplateListGroups(filteredRows),
    [filteredRows],
  );

  const defaultCollapsedSubgroupKeys = useMemo(() => {
    const keys = new Set<string>();
    groups.forEach((group) => {
      if ((group.subgroups?.length ?? 0) <= 1) return;
      group.subgroups?.forEach((subgroup) => {
        keys.add(templateListSubgroupCollapseKey(group.typeKey, subgroup.key));
      });
    });
    return keys;
  }, [groups]);

  const forceExpandAll = searchQuery.trim().length > 0;

  const effectiveCollapsedSubgroupKeys = forceExpandAll
    ? new Set<string>()
    : (collapsedSubgroupKeys ?? defaultCollapsedSubgroupKeys);

  const isTypeExpanded = (typeKey: string) =>
    forceExpandAll || !collapsedTypeKeys.has(typeKey);

  const isSubgroupExpanded = (typeKey: string, subgroupKey: string) =>
    forceExpandAll ||
    !effectiveCollapsedSubgroupKeys.has(
      templateListSubgroupCollapseKey(typeKey, subgroupKey),
    );

  const toggleTypeCollapsed = (typeKey: string) => {
    setCollapsedTypeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(typeKey)) next.delete(typeKey);
      else next.add(typeKey);
      return next;
    });
  };

  const toggleSubgroupCollapsed = (typeKey: string, subgroupKey: string) => {
    const key = templateListSubgroupCollapseKey(typeKey, subgroupKey);
    setCollapsedSubgroupKeys((prev) => {
      const next = new Set(prev ?? defaultCollapsedSubgroupKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isEmpty = filteredRows.length === 0;

  const filteredIssueCount = useMemo(
    () => countTemplatesWithDocumentIssues(filteredRows),
    [filteredRows],
  );

  const typeFilterOptions = useMemo<TypeFilterOption[]>(
    () => [
      { value: ALL_TYPES_FILTER, label: 'All types' },
      ...sortTemplateTypesByRank().map((type) => ({
        value: type,
        label: formatKidTemplateTypeLabel(type),
      })),
    ],
    [],
  );

  const selectedTypeFilter =
    typeFilterOptions.find((option) => option.value === typeFilter) ??
    typeFilterOptions[0];

  const selectedStatusFilter =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ??
    STATUS_FILTER_OPTIONS[0];

  const columns = useMemo<ColumnDef<TemplateListTableRow>[]>(
    () => [
      { id: 'name', header: 'Template name' },
      { id: 'status', header: 'Status' },
      { id: 'actions', header: 'Actions' },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderTemplateRow = (row: TemplateListTableRow) => {
    const name = row.template.template_name || row.template.template_id;

    return (
      <tr
        key={row.template.template_id}
        className="border-b border-gray-200 hover:bg-gray-50"
      >
        <td className="px-4 py-2.5 text-sm">
          <span className="font-bold text-gray-900" title={name}>
            {name}
          </span>
        </td>
        <td className="px-4 py-2.5 text-sm">
          <TemplateSetupStatusPills
            setupStatus={row.setupStatus}
            missingCount={row.missingDocumentCount}
            invalidCount={row.invalidDocumentCount}
          />
        </td>
        <td className="px-4 py-2.5 text-sm">
          <button
            type="button"
            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-50"
            onClick={() => onSelectTemplate(row.template.template_id)}
            title="Edit template"
          >
            Edit
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="shrink-0 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">
              All templates
            </h2>
            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-mainblue focus:outline-none focus:ring-2 focus:ring-mainblue/30"
              aria-label="How template management works"
              title="How template management works"
            >
              <HiQuestionMarkCircle className="h-5 w-5" aria-hidden />
            </button>
            <TemplateIssueCount
              issueCount={filteredIssueCount}
              isUpdating={isUpdatingCounts}
            />
          </div>

          <div className="flex items-center gap-2">
            <SearchBar
              searchHandler={setSearchQuery}
              placeholder="Search templates…"
              className="w-64 shrink-0"
            />
            <div className="inline-flex shrink-0">
              <GenericDropdown
                data={typeFilterOptions}
                selected={selectedTypeFilter}
                setSelected={(option) => setTypeFilter(option.value)}
                getDisplayValue={(option) => option.label}
                getDisplayNode={(option) =>
                  option.value === ALL_TYPES_FILTER ? (
                    option.label
                  ) : (
                    <TemplateTypeChip templateType={option.value} size="sm" />
                  )
                }
                label={
                  selectedTypeFilter.value === ALL_TYPES_FILTER ? (
                    selectedTypeFilter.label
                  ) : (
                    <TemplateTypeChip
                      templateType={selectedTypeFilter.value}
                      size="sm"
                    />
                  )
                }
                truncateLabel={selectedTypeFilter.value === ALL_TYPES_FILTER}
                tooltip="Filter by template type"
                className={listToolbarDropdownClassName}
                dropdownClassName={listToolbarDropdownMenuClassName}
              />
            </div>
            <div className="inline-flex shrink-0">
              <GenericDropdown
                data={STATUS_FILTER_OPTIONS}
                selected={selectedStatusFilter}
                setSelected={(option) => setStatusFilter(option.value)}
                getDisplayValue={(option) => option.label}
                getDisplayNode={(option) => (
                  <StatusFilterLabel
                    status={option.value}
                    label={option.label}
                  />
                )}
                label={
                  <StatusFilterLabel
                    status={selectedStatusFilter.value}
                    label={selectedStatusFilter.label}
                  />
                }
                truncateLabel={
                  selectedStatusFilter.value === ALL_STATUSES_FILTER
                }
                tooltip="Filter by setup status"
                className={listToolbarDropdownClassName}
                dropdownClassName={listToolbarDropdownMenuClassName}
              />
            </div>
            <button
              type="button"
              className={btnPrimary}
              onClick={onStartCreateTemplate}
            >
              Create template
            </button>
          </div>
        </div>
      </div>

      <GenericModal
        isOpen={showHelpModal}
        setIsOpen={setShowHelpModal}
        title="How template management works"
        className="max-w-2xl"
      >
        <TemplateMoreInformationContent />
      </GenericModal>

      <div className="min-h-0 flex-1 px-4 pb-4 pt-3">
        <div className="h-full min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-[52%]" />
            <col className="w-[32%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead className="sticky top-0 z-[1] bg-mainblue text-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white">
            {isEmpty ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-sm text-gray-500"
                >
                  {rows.length === 0
                    ? 'No templates yet. Use Create template to add one.'
                    : 'No templates match your search or filter.'}
                </td>
              </tr>
            ) : (
              groups.map((group) => {
                const typeExpanded = isTypeExpanded(group.typeKey);
                const TypeChevron = typeExpanded
                  ? HiChevronDown
                  : HiChevronRight;
                const subgroups = group.subgroups ?? [];
                const hasReferenceGroups = subgroups.length > 0;

                return (
                  <Fragment key={`type-${group.typeKey}`}>
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="border-b border-gray-200 p-0"
                      >
                        <button
                          type="button"
                          onClick={() => toggleTypeCollapsed(group.typeKey)}
                          className="flex w-full items-center gap-2.5 bg-gray-100 px-4 py-2 text-left transition-colors hover:bg-gray-200/80"
                          aria-expanded={typeExpanded}
                        >
                          <TypeChevron
                            className="h-4 w-4 shrink-0 text-gray-500"
                            aria-hidden
                          />
                          <TemplateTypeChip
                            templateType={group.typeKey}
                            size="sm"
                            className="font-medium"
                          />
                        </button>
                      </td>
                    </tr>

                    {typeExpanded &&
                      (hasReferenceGroups
                        ? subgroups.map((subgroup) => {
                            const subgroupExpanded = isSubgroupExpanded(
                              group.typeKey,
                              subgroup.key,
                            );
                            const SubChevron = subgroupExpanded
                              ? HiChevronDown
                              : HiChevronRight;

                            return (
                              <Fragment
                                key={`subgroup-${group.typeKey}-${subgroup.key}`}
                              >
                                <tr>
                                  <td
                                    colSpan={columns.length}
                                    className="border-b border-gray-200 p-0"
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleSubgroupCollapsed(
                                          group.typeKey,
                                          subgroup.key,
                                        )
                                      }
                                      className="flex w-full min-w-0 items-center gap-2.5 bg-gray-50 px-4 py-2 pl-8 text-left transition-colors hover:bg-gray-100"
                                      aria-expanded={subgroupExpanded}
                                    >
                                      <SubChevron
                                        className="h-4 w-4 shrink-0 text-gray-500"
                                        aria-hidden
                                      />
                                      <span
                                        className="min-w-0 truncate text-sm font-medium text-gray-800"
                                        title={subgroup.label}
                                      >
                                        {subgroup.label}
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                                {subgroupExpanded
                                  ? subgroup.items.map((row) =>
                                      renderTemplateRow(row),
                                    )
                                  : null}
                              </Fragment>
                            );
                          })
                        : group.items.map((row) => renderTemplateRow(row)))}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default TemplateListPanel;
